/**
 * AP-32 — Beleg anlegen: nur die Art. Empfänger, Datum und alles Weitere schreibt
 * die Maske (Nachtrag 2). Angeboten werden nur Arten, die eingeschaltet sind und
 * die der Benutzer anlegen darf — dieselbe Funktion wie im Dienst (`documentKindsWith`).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { documentKindsWith, type DocumentKind } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { documentsApi } from '../api/documents.api'
import { ARTGRUPPEN } from '../lib/artgruppen'
import type { KindInfo } from '../types/document.types'

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
  vorgabeArt?: DocumentKind
}

export function CreateDocumentDialog({ open, onClose, onCreated, vorgabeArt }: Props) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [arten, setArten] = useState<KindInfo[]>([])
  const [kind, setKind] = useState<DocumentKind | ''>('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)

  useEffect(() => {
    if (!open) return
    setFehler(null)
    const erlaubt = new Set(documentKindsWith('create', user?.permissions))
    documentsApi
      .kinds()
      .then(({ data }) => {
        // Ein Storno entsteht nur aus der Rechnung, die es aufhebt (8.3) — ohne
        // Bezug wäre es eine negative Rechnung ohne Gegenstück.
        const angebot = data.data.filter((k) => k.isEnabled && erlaubt.has(k.kind) && k.kind !== 'CANCELLATION')
        setArten(angebot)
        setKind((vorher) => vorher || (vorgabeArt && angebot.some((a) => a.kind === vorgabeArt) ? vorgabeArt : (angebot[0]?.kind ?? '')))
      })
      .catch((err) => setFehler(fehlertext(err, t)))
  }, [open, user?.permissions, vorgabeArt, t])

  if (!open) return null

  const absenden = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!kind) return
    setFehler(null)
    setSpeichert(true)
    try {
      const { data } = await documentsApi.create({ kind })
      onClose()
      onCreated(data.data.id)
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-border shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <form onSubmit={absenden} className="space-y-4 p-5">
          <h2 className="text-lg font-semibold">{t('documents.create')}</h2>
          {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}

          <div>
            <label className="mb-1 block text-sm font-medium">{t('documents.fields.kind')}</label>
            <select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)} className={eingabe} autoFocus>
              {/* N-60: gegliedert nach Vorgang — leere Gruppen entfallen. */}
              {ARTGRUPPEN.map((g) => {
                const inGruppe = arten.filter((a) => g.arten.includes(a.kind))
                if (!inGruppe.length) return null
                return (
                  <optgroup key={g.key} label={t(`documents.kindGroups.${g.key}`)}>
                    {inGruppe.map((a) => (
                      <option key={a.kind} value={a.kind}>
                        {t(`documents.kinds.${a.kind}`)}
                      </option>
                    ))}
                  </optgroup>
                )
              })}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">{t('documents.head.createHint')}</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm hover:bg-muted/50">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={speichert || !kind}
              className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {/* N-105: Der Beleg öffnet sich vorläufig — angelegt ist er erst mit „Speichern". */}
              {t('documents.createOpen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
